import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";
import { WhipLogo } from "@/components/WhipLogo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-orange-500/60";

export default function SetPassword({ voluntary = false }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError("The two new passwords do not match."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: form.current, new_password: form.next });
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      toast.success(voluntary ? "Password changed. A confirmation email is on its way." : "Password updated. Welcome to WHIP.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "var(--whip-bg)" }} data-testid="set-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><WhipLogo size={40} /></div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 backdrop-blur">
          <div className="flex items-center gap-2 text-orange-400 text-xs font-mono uppercase tracking-widest mb-3">
            <KeyRound className="w-4 h-4" /> {voluntary ? "Account security" : "Action required"}
          </div>
          <h1 className="text-2xl font-bold text-white">{voluntary ? "Change your password" : "Set your password"}</h1>
          <p className="text-sm text-white/55 mt-2">
            {voluntary
              ? `Signed in as ${user?.email}. Enter your current password, then choose a new one: at least 10 characters with a letter and a number, and not one of your last four passwords. Other devices will be signed out.`
              : `${user?.email} is signed in with a temporary password. Choose a new one to continue: at least 10 characters with a letter and a number, and not one of your last four passwords.`}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input type="password" autoComplete="current-password" placeholder={voluntary ? "Current password" : "Temporary password"} value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })} className={inputCls} required data-testid="set-password-current-input" />
            <input type="password" autoComplete="new-password" placeholder="New password" value={form.next} minLength={10}
              onChange={(e) => setForm({ ...form, next: e.target.value })} className={inputCls} required data-testid="set-password-new-input" />
            <input type="password" autoComplete="new-password" placeholder="Confirm new password" value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} required data-testid="set-password-confirm-input" />
            {error && <p className="text-sm text-rose-300" data-testid="set-password-error">{error}</p>}
            <button type="submit" disabled={submitting}
              className="w-full rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:from-orange-500 hover:to-orange-400 transition-colors disabled:opacity-60"
              data-testid="set-password-submit-button">
              {submitting ? "Saving…" : "Save new password"}
            </button>
          </form>
          {voluntary ? (
            <button type="button" onClick={() => navigate(-1)} className="mt-5 text-xs text-white/40 hover:text-white/70 transition-colors" data-testid="change-password-cancel-button">
              Cancel
            </button>
          ) : (
            <button type="button" onClick={logout} className="mt-5 flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors" data-testid="set-password-signout-button">
              <LogOut className="w-3.5 h-3.5" /> Sign out instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
